// SPDX-License-Identifier: AGPL-3.0-or-later

import type {View} from '@app/features/channel/components/pickers/gif/GifPickerTypes';
import type {Gif, GifFeatured} from '@app/features/expressions/commands/GifCommands';
import * as GifCommands from '@app/features/expressions/commands/GifCommands';
import {makeAutoObservable, runInAction} from 'mobx';

interface MiniDebouncedFunc<TArgs extends Array<unknown>> {
	(...args: TArgs): void;
	cancel(): void;
}
function createDebounced<TArgs extends Array<unknown>>(
	fn: (...args: TArgs) => unknown,
	wait: number,
): MiniDebouncedFunc<TArgs> {
	let timer: ReturnType<typeof setTimeout> | null = null;
	const debounced = ((...args: TArgs) => {
		if (timer !== null) clearTimeout(timer);
		timer = setTimeout(() => {
			timer = null;
			fn(...args);
		}, wait);
	}) as MiniDebouncedFunc<TArgs>;
	debounced.cancel = () => {
		if (timer !== null) {
			clearTimeout(timer);
			timer = null;
		}
	};
	return debounced;
}
export class GifPickerState {
	featured: GifFeatured = {categories: [], gifs: []};
	gifs: Array<Gif> = [];
	loading = false;
	initialFeaturedLoading = false;
	searchTerm = '';
	previousSearchTerm = '';
	suggestions: Array<string> = [];
	view: View = 'default';
	pendingView: View | null = null;
	hasSearchResults = false;
	shouldRenderSearchResults = false;
	private disposed = false;
	private searchDebounced: MiniDebouncedFunc<[string]>;
	private suggestDebounced: MiniDebouncedFunc<[string]>;
	constructor() {
		makeAutoObservable<GifPickerState, 'searchDebounced' | 'suggestDebounced' | 'disposed'>(
			this,
			{
				searchDebounced: false,
				suggestDebounced: false,
				disposed: false,
			},
			{autoBind: true},
		);
		this.searchDebounced = createDebounced((term: string) => this.performSearch(term), 400);
		this.suggestDebounced = createDebounced((term: string) => this.performSuggest(term), 300);
	}
	dispose() {
		this.disposed = true;
		this.searchDebounced.cancel();
		this.suggestDebounced.cancel();
	}
	get isLandingPage(): boolean {
		return this.view === 'default' && !this.searchTerm.trim();
	}
	get isShowingFeatured(): boolean {
		return this.view === 'default' && (!this.searchTerm.trim() || !this.shouldRenderSearchResults);
	}
	get isShowingFavorites(): boolean {
		return this.view === 'favorites';
	}
	get gifsToRender(): Array<Gif> {
		return this.gifs;
	}
	get shouldShowNoResults(): boolean {
		if (this.loading) return false;
		if (!this.shouldRenderSearchResults) return false;
		if (this.view === 'trending') return this.gifs.length === 0;
		if (this.view === 'default') {
			return Boolean(this.searchTerm.trim()) && this.hasSearchResults === false;
		}
		return false;
	}
	goToDefaultView() {
		this.pendingView = null;
		this.view = 'default';
		if (!this.searchTerm.trim()) {
			this.shouldRenderSearchResults = false;
			this.loading = false;
		}
	}
	goToFavorites() {
		this.pendingView = null;
		this.searchDebounced.cancel();
		this.suggestDebounced.cancel();
		this.view = 'favorites';
		this.searchTerm = '';
		this.previousSearchTerm = '';
		this.suggestions = [];
		this.gifs = [];
		this.hasSearchResults = false;
		this.shouldRenderSearchResults = false;
		this.loading = false;
	}
	goToTrending() {
		if (this.pendingView === 'trending') return;
		if (this.view === 'trending' && this.gifs.length > 0) return;
		this.pendingView = 'trending';
		this.loading = true;
		this.searchTerm = '';
		this.previousSearchTerm = '';
		this.suggestions = [];
		this.hasSearchResults = false;
		this.shouldRenderSearchResults = true;
		this.searchDebounced.cancel();
		this.suggestDebounced.cancel();
		void this.loadTrending();
	}
	async ensureFeaturedLoaded() {
		if (this.view !== 'default') return;
		if (this.featured.categories.length > 0 || this.featured.gifs.length > 0) return;
		this.loading = true;
		this.initialFeaturedLoading = true;
		try {
			const data = await GifCommands.getFeatured();
			runInAction(() => {
				if (this.disposed) return;
				if (this.view !== 'default') return;
				this.featured = data;
				this.loading = false;
				this.initialFeaturedLoading = false;
			});
		} catch {
			runInAction(() => {
				if (this.disposed) return;
				if (this.view !== 'default') return;
				this.loading = false;
				this.initialFeaturedLoading = false;
			});
		}
	}
	private async loadTrending() {
		try {
			const results = await GifCommands.getTrending();
			runInAction(() => {
				if (this.disposed) return;
				if (this.pendingView !== 'trending') return;
				this.view = 'trending';
				this.pendingView = null;
				this.gifs = results;
				this.loading = false;
				this.hasSearchResults = results.length > 0;
			});
		} catch {
			runInAction(() => {
				if (this.disposed) return;
				if (this.pendingView !== 'trending') return;
				this.pendingView = null;
				this.loading = false;
			});
		}
	}
	setSearchTerm(term: string) {
		const limited = term.slice(0, 100);
		if (!limited.trim()) {
			this.searchTerm = limited;
			this.resetSearch();
			return;
		}
		this.previousSearchTerm = this.searchTerm;
		this.searchTerm = limited;
		this.loading = true;
		this.shouldRenderSearchResults = true;
		const trimmed = limited.trim();
		this.searchDebounced(trimmed);
		this.triggerSuggestions();
	}
	resetSearch() {
		this.searchDebounced.cancel();
		this.suggestDebounced.cancel();
		this.searchTerm = '';
		this.previousSearchTerm = '';
		this.gifs = [];
		this.suggestions = [];
		this.hasSearchResults = false;
		this.loading = false;
		this.shouldRenderSearchResults = false;
	}
	triggerSuggestions() {
		const trimmed = this.searchTerm.trim();
		if (!trimmed) {
			runInAction(() => {
				this.suggestions = [];
			});
			return;
		}
		this.suggestDebounced(trimmed);
	}
	flushSearch() {
		this.searchDebounced.cancel();
		const term = this.searchTerm.trim();
		if (term) {
			this.loading = true;
			this.shouldRenderSearchResults = true;
			void this.performSearch(term);
		}
	}
	private async performSearch(term: string) {
		if (!term) {
			runInAction(() => this.resetSearch());
			return;
		}
		try {
			const results = await GifCommands.search(term);
			runInAction(() => {
				if (this.disposed) return;
				if (this.searchTerm.trim() !== term) return;
				this.gifs = results;
				this.loading = false;
				this.hasSearchResults = results.length > 0;
				this.shouldRenderSearchResults = true;
			});
		} catch {
			runInAction(() => {
				if (this.disposed) return;
				if (this.searchTerm.trim() !== term) return;
				this.loading = false;
			});
		}
	}
	private async performSuggest(term: string) {
		if (!term) {
			runInAction(() => {
				this.suggestions = [];
			});
			return;
		}
		try {
			const suggestions = await GifCommands.suggest(term);
			runInAction(() => {
				if (this.disposed) return;
				if (this.searchTerm.trim() !== term) return;
				this.suggestions = suggestions;
			});
		} catch {}
	}
}
